import React, { useState, forwardRef } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import emailjs from '@emailjs/browser';
import 'react-datepicker/dist/react-datepicker.css';

const AppointmentSection = styled.section`
  padding: 120px ${({ theme }) => theme.spacing.lg};
  background: 
    linear-gradient(135deg, rgba(0, 102, 204, 0.05) 0%, rgba(0, 168, 168, 0.05) 100%),
    linear-gradient(180deg, ${({ theme }) => theme.colors.white} 0%, ${({ theme }) => theme.colors.background} 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: radial-gradient(circle at 50% 0%, rgba(0, 168, 168, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 80px ${({ theme }) => theme.spacing.md};
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SectionDescription = styled.p`
  color: ${({ theme }) => theme.colors.grey};
  font-size: ${({ theme }) => theme.fontSizes.lg};
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  box-shadow: 0 20px 60px rgba(0, 102, 204, 0.15);
  border: 1px solid rgba(0, 102, 204, 0.1);
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.error};
  margin-left: 4px;
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $error }) => $error ? theme.colors.error : theme.colors.lightGrey};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  transition: border-color ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.grey};
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid ${({ theme, $error }) => $error ? theme.colors.error : theme.colors.lightGrey};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  transition: border-color ${({ theme }) => theme.transitions.fast};
  background-color: #ffffff;
  cursor: pointer;
  height: 48px;
  width: 100%;
  color: #333333;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  option {
    color: #333333;
    background-color: #ffffff;
    padding: 10px;
  }
`;

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  resize: vertical;
  min-height: 100px;
  font-family: ${({ theme }) => theme.fonts.primary};
  transition: border-color ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.grey};
  }
`;

const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: -${({ theme }) => theme.spacing.xs};
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.base};
  margin-top: ${({ theme }) => theme.spacing.sm};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  box-shadow: ${({ theme }) => theme.shadows['2xl']};
  z-index: 1000;
  text-align: center;
  max-width: 400px;
  width: 90%;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -60%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const CloseButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border: 2px solid ${({ theme, $error }) => $error ? theme.colors.error : theme.colors.lightGrey};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: ${({ theme }) => theme.fontSizes.base};
    font-family: ${({ theme }) => theme.fonts.primary};
    cursor: pointer;
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
  
  .react-datepicker {
    font-family: ${({ theme }) => theme.fonts.primary};
    border: 2px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }
  
  .react-datepicker__header {
    background-color: ${({ theme }) => theme.colors.primary};
    border-bottom: none;
    border-radius: ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md} 0 0;
  }
  
  .react-datepicker__current-month,
  .react-datepicker__day-name {
    color: ${({ theme }) => theme.colors.white};
  }
  
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const timeSlots = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM'
];

const services = [
  'Medical Billing Services',
  'Medical Coding',
  'Accounts Receivable Management',
  'Healthcare Automation',
  'Virtual Clinical Solutions',
  'Claims Management',
  'General Inquiry'
];

export const AppointmentForm = forwardRef((props, ref) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: null,
    timeSlot: '',
    service: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[\d\s\-+()]{10,}$/;
    return re.test(phone);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, date }));
    if (errors.date) {
      setErrors(prev => ({ ...prev, date: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.date) {
      newErrors.date = 'Please select a date';
    }

    if (!formData.timeSlot) {
      newErrors.timeSlot = 'Please select a time slot';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Format the date for the email
      const formattedDate = formData.date ? formData.date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) : '';

      // Prepare email template parameters
      const templateParams = {
        to_email: 'sid@logghealthcare.com',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        date: formattedDate,
        time_slot: formData.timeSlot,
        service: formData.service,
        message: formData.message || 'No additional message provided',
      };

      // Send email using EmailJS
      // Note: Replace these with your actual EmailJS credentials
      await emailjs.send(
        'service_ekj2jhn',    // Replace with your EmailJS service ID
        'template_jr5gp5p',   // Replace with your EmailJS template ID
        templateParams,
        'mnh7IWAlyJKSOgINk'     // Replace with your EmailJS public key
      );

      console.log('Appointment request sent:', formData);
      setIsSubmitting(false);
      setShowSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: null,
        timeSlot: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      setIsSubmitting(false);
      alert('Failed to send request. Please try again or contact us directly at sid@logghealthcare.com');
    }
  };

  const closeModal = () => {
    setShowSuccess(false);
  };

  return (
    <>
      <AppointmentSection id="appointment" ref={ref}>
        <Container>
          <SectionHeader>
            <SectionTitle>Request a Consultation</SectionTitle>
            <SectionDescription>
              Let's discuss how we can optimize your revenue cycle management
            </SectionDescription>
          </SectionHeader>
          
          <FormCard>
            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="name">
                    Full Name<Required>*</Required>
                  </Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    $error={errors.name}
                    aria-required="true"
                    aria-invalid={!!errors.name}
                  />
                  {errors.name && <ErrorMessage role="alert">{errors.name}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">
                    Email<Required>*</Required>
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    $error={errors.email}
                    aria-required="true"
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && <ErrorMessage role="alert">{errors.email}</ErrorMessage>}
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <Label htmlFor="phone">
                  Phone Number<Required>*</Required>
                </Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  $error={errors.phone}
                  aria-required="true"
                  aria-invalid={!!errors.phone}
                />
                {errors.phone && <ErrorMessage role="alert">{errors.phone}</ErrorMessage>}
              </FormGroup>
              
              <FormRow>
                <FormGroup>
                  <Label htmlFor="date">
                    Preferred Consultation Date<Required>*</Required>
                  </Label>
                  <DatePickerWrapper $error={errors.date}>
                    <DatePicker
                      id="date"
                      selected={formData.date}
                      onChange={handleDateChange}
                      minDate={new Date()}
                      dateFormat="MMMM d, yyyy"
                      placeholderText="Select a date"
                      aria-required="true"
                      aria-invalid={!!errors.date}
                    />
                  </DatePickerWrapper>
                  {errors.date && <ErrorMessage role="alert">{errors.date}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="timeSlot">
                    Time Slot<Required>*</Required>
                  </Label>
                  <Select
                    id="timeSlot"
                    name="timeSlot"
                    value={formData.timeSlot}
                    onChange={handleChange}
                    $error={errors.timeSlot}
                    aria-required="true"
                    aria-invalid={!!errors.timeSlot}
                  >
                    <option value="">Select a time</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </Select>
                  {errors.timeSlot && <ErrorMessage role="alert">{errors.timeSlot}</ErrorMessage>}
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <Label htmlFor="service">
                  Service<Required>*</Required>
                </Label>
                <Select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  $error={errors.service}
                  aria-required="true"
                  aria-invalid={!!errors.service}
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </Select>
                {errors.service && <ErrorMessage role="alert">{errors.service}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="message">
                  Tell Us About Your Practice (Optional)
                </Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Practice size, current billing challenges, specific needs..."
                />
              </FormGroup>
              
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Request Consultation'}
              </SubmitButton>
            </Form>
          </FormCard>
        </Container>
      </AppointmentSection>
      
      {showSuccess && (
        <>
          <ModalOverlay onClick={closeModal} />
          <SuccessModal role="dialog" aria-labelledby="success-title" aria-modal="true">
            <SuccessIcon aria-hidden="true">✓</SuccessIcon>
            <h3 id="success-title">Consultation Request Received!</h3>
            <p>Thank you for your interest! Our team will contact you shortly to schedule your consultation and discuss how we can help optimize your revenue cycle.</p>
            <CloseButton onClick={closeModal}>Close</CloseButton>
          </SuccessModal>
        </>
      )}
    </>
  );
});

AppointmentForm.displayName = 'AppointmentForm';
